import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PassOfferSummary } from '../../models/Pass';
import {
  formatPassMoney,
  minutesToDisplayHours,
  passEntitlementLabel,
} from '../../utils/passFormatting';
import PassOfferStatusChip from './PassOfferStatusChip';

export interface RelatedPassRowProps {
  pass: PassOfferSummary;
  onClick: () => void;
}

const RelatedPassRow: React.FC<RelatedPassRowProps> = ({ pass, onClick }) => {
  const { t, i18n } = useTranslation();
  const currency = pass.currencyCode || 'PLN';
  const packPrice = formatPassMoney(pass.fixedPrice, currency, i18n.language);
  const unitCount =
    pass.unitType === 'MINUTE' ? minutesToDisplayHours(pass.quantity) : pass.quantity;
  const perUnitPrice =
    unitCount > 0
      ? formatPassMoney(Math.round((pass.fixedPrice / unitCount) * 100) / 100, currency, i18n.language)
      : null;
  const perUnitLabel = perUnitPrice
    ? pass.unitType === 'MINUTE'
      ? t('spaceDetails.pricePerHour', '{{price}} / hour', { price: perUnitPrice })
      : t('hostLessonOptions.details.pricePerLesson', '{{price}} / lesson', { price: perUnitPrice })
    : null;
  const caption = [packPrice, passEntitlementLabel(pass, t), perUnitLabel].filter(Boolean).join(' · ');

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1,
        py: 0.75,
        minWidth: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.selected',
          borderColor: 'text.disabled',
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, lineHeight: 1.25 }}>
            {pass.title}
          </Typography>
          <PassOfferStatusChip status={pass.status} />
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.3 }}>
          {caption}
        </Typography>
      </Box>
    </Box>
  );
};

export default RelatedPassRow;
